import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Gamepad2, Printer, House } from "lucide-react";
import { Reveal, Kicker, CTAButton, Section } from "@/components/landing/kit";
import { CASE_STUDIES } from "@/lib/site-data";

const ICONS = { cart: ShoppingCart, toy: Gamepad2, office: Printer, home: House };

const Card = ({ cs, index }) => {
  const Icon = ICONS[cs.icon] || ShoppingCart;
  return (
    <Reveal
      delay={(index % 2) * 0.08}
      className={`group relative min-h-[300px] overflow-hidden rounded-3xl border border-[#E5E8F0] bg-[#F8FAFD] shadow-sm transition-all duration-300 hover:border-[#2D5BFF]/40 hover:shadow-md ${cs.span} sm:min-h-[340px]`}
      data-testid={`case-study-${cs.icon}`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(45,91,255,0.10),transparent_58%)]" />
      <Icon
        className="absolute -right-6 -top-6 h-44 w-44 text-[#0F1117]/[0.05] transition-transform duration-700 group-hover:scale-110"
        strokeWidth={1.2}
      />

      <div className="relative flex h-full flex-col justify-between p-7 sm:p-9">
        <span className="flex w-fit items-center gap-2 rounded-full border border-[#D6DBE7] bg-white px-3 py-1.5 font-heading text-[11px] font-bold uppercase tracking-widest text-[#363A45] shadow-sm">
          <Icon className="h-3.5 w-3.5 text-[#2D5BFF]" />
          {cs.tag}
        </span>
        <div>
          <p className="font-heading text-6xl font-black tracking-tight text-[#2D5BFF] sm:text-7xl">
            {cs.metricValue}
          </p>
          <p className="mt-2 font-heading text-lg font-bold uppercase tracking-tight text-[#0F1117]">
            {cs.metricLabel}
          </p>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-[#5B6375]">{cs.sub}</p>
        </div>
      </div>
    </Reveal>
  );
};

const CaseStudies = () => (
  <Section id="case-studies" className="border-b border-[#E5E8F0] bg-white py-24 sm:py-32">
    <Reveal className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div className="max-w-2xl">
        <Kicker className="mb-5">Documented results · hundreds of managed stores</Kicker>
        <h2 className="text-balance font-heading text-4xl font-black leading-[0.98] tracking-tight text-[#0F1117] sm:text-6xl">
          We don&apos;t pitch potential. We show receipts.
        </h2>
      </div>
      <p className="max-w-sm text-sm leading-relaxed text-[#4B5262]">
        Grocery, toys, office supplies and everyday household goods — real stores, real
        numbers. Every partnership is measured in profit.
      </p>
    </Reveal>

    <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
      {CASE_STUDIES.map((cs, i) => (
        <Card key={cs.tag} cs={cs} index={i} />
      ))}
    </div>

    <Reveal delay={0.1} className="mt-12 flex justify-center">
      <CTAButton size="lg" testId="casestudies-cta-button">
        See What We Can Do For You
      </CTAButton>
    </Reveal>

    <p className="mx-auto mt-6 max-w-2xl text-center text-xs leading-relaxed text-[#8A909E]">
      Real, individual client results shown above. These outcomes are not typical and your
      results will vary.{" "}
      <Link
        to="/earnings-claims"
        className="text-[#646B7C] underline underline-offset-2 hover:text-[#2D5BFF]"
      >
        See our Earnings Claims Statement.
      </Link>
    </p>
  </Section>
);

export default CaseStudies;
